import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { 
  ArrowRight, 
  MessageSquare, 
  UserPlus, 
  Tag, 
  Phone, 
  DollarSign, 
  AlertTriangle,
  FileEdit,
  PlusCircle,
  Clock
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Activity type icons and colors
const ACTIVITY_CONFIG = {
  status_change: {
    icon: ArrowRight,
    color: 'bg-blue-100 text-blue-600',
    label: 'Stage Change'
  },
  note_added: {
    icon: MessageSquare,
    color: 'bg-slate-100 text-slate-600',
    label: 'Note Added'
  },
  assignment_change: {
    icon: UserPlus,
    color: 'bg-purple-100 text-purple-600',
    label: 'Assignment'
  },
  tag_change: {
    icon: Tag,
    color: 'bg-amber-100 text-amber-600',
    label: 'Tags'
  },
  communication: {
    icon: Phone,
    color: 'bg-green-100 text-green-600',
    label: 'Communication'
  },
  deposit_received: {
    icon: DollarSign,
    color: 'bg-emerald-100 text-emerald-600',
    label: 'Deposit'
  },
  no_show_flagged: {
    icon: AlertTriangle,
    color: 'bg-red-100 text-red-600',
    label: 'No-Show'
  },
  field_update: {
    icon: FileEdit,
    color: 'bg-slate-100 text-slate-600',
    label: 'Update'
  },
  created: {
    icon: PlusCircle,
    color: 'bg-green-100 text-green-600',
    label: 'Created'
  }
};

// Stage labels for display
const STAGE_LABELS = {
  cold_data: 'Cold Data',
  attempting_contact: 'Attempting Contact',
  contacted: 'Contacted',
  qualified: 'Qualified',
  consultation_scheduled: 'Consultation Scheduled',
  consultation_completed: 'Consultation Completed',
  warm_follow_up: 'Warm Follow-Up',
  rsvp_office_event: 'RSVP Office Event',
  attended_office_event: 'Attended Office Event',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  long_term_nurture: 'Long-Term Nurture'
};

export default function ActivityHistoryModal({ isOpen, onClose, leadId, leadName }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && leadId) {
      fetchActivities();
    }
  }, [isOpen, leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}/activities`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }
      
      const data = await response.json();
      setActivities(data);
    } catch (err) {
      logger.error('Error fetching activities:', err);
      setError('Unable to load activity history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const formatRelativeDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return '';
    }
  };

  const ActivityItem = ({ activity }) => {
    const config = ACTIVITY_CONFIG[activity.activity_type] || ACTIVITY_CONFIG.field_update;
    const Icon = config.icon;

    return (
      <div className="flex gap-4 py-4 border-b border-slate-100 last:border-0" data-testid={`activity-${activity.activity_id}`}>
        {/* Icon */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${config.color}`}>
          <Icon className="h-5 w-5" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <Badge variant="outline" className="text-[10px] mb-1">
                {config.label}
              </Badge>
              <p className="text-sm font-medium text-slate-900">{activity.description}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-slate-400">{formatRelativeDate(activity.created_at)}</p>
              <p className="text-[10px] text-slate-300">{formatDate(activity.created_at)}</p>
            </div>
          </div>
          
          {/* Notes */}
          {activity.notes && (
            <div className="mt-2 p-3 bg-slate-50 rounded-lg text-sm text-slate-600 italic">
              "{activity.notes}"
            </div>
          )}
          
          {/* User */}
          <p className="mt-2 text-xs text-slate-400">
            by {activity.created_by_name || 'System'}
          </p>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="activity-history-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-400" />
            Activity History
            {leadName && <span className="text-slate-400 font-normal">- {leadName}</span>}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto mt-4 pr-2" style={{ maxHeight: '60vh' }}>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex gap-4 py-4 border-b border-slate-100">
                  <Skeleton className="w-10 h-10 rounded-xl flex-shrink-0" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-3" />
              <p className="text-slate-500">{error}</p>
              <button 
                onClick={fetchActivities}
                className="mt-3 text-sm text-blue-600 hover:underline"
              >
                Try again
              </button>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No activity history yet</p>
              <p className="text-xs text-slate-400 mt-1">Activities will appear here as you work with this lead</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {activities.map((activity) => (
                <ActivityItem key={activity.activity_id} activity={activity} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
